import moment from 'moment';
import _ from 'underscore';
import API from '@api/liveSession.api';
import userApi from '@api/user.api';

function initialState() {
  return {
    attachmentsTabLoading: false,
    currentGuestId: null,
    mapCategoriesLoading: false,
    messagesTabLoading: false,
    noteTabLoading: false,
    liveSessionLoading: false,
    liveSession: {
      attachments: [],
      defaultPins: [],
      mapCategories: [],
      messages: [],
      note: '',
      record: {},
      notFound: false
    }
  };
}

const getters = {
  attachments: (state) => state.liveSession.attachments,
  attachmentsTabLoading: (state) => state.attachmentsTabLoading,
  currentGuestId: (state) => state.currentGuestId,
  defaultPins: (state) => state.liveSession.defaultPins,
  liveSession: (state) => state.liveSession.record,
  liveSessionLoading: (state) => state.liveSessionLoading,
  liveSessionNotFound: (state) => state.liveSession.notFound,
  mapCategories: (state) => state.liveSession.mapCategories,
  mapCategoriesLoading: (state) => state.mapCategoriesLoading,
  messages: (state) => state.liveSession.messages,
  messagesTabLoading: (state) => state.messagesTabLoading,
  note: (state) => state.liveSession.note,
  noteTabLoading: (state) => state.liveSession.record.noteTabLoading
};

const mutations = {
  SET_ATTACHMENTS(state, data) {
    state.liveSession.attachments = data;
  },
  SET_ATTACHMENTS_TAB_LOADING(state, loading) {
    state.attachmentsTabLoading = loading;
  },
  SET_CURRENT_GUEST_ID(state, data) {
    state.currentGuestId = data;
  },
  SET_DEFAULT_PINS(state, data) {
    state.liveSession.defaultPins = data;
  },
  SET_LIVE_SESSION(state, data) {
    state.liveSession.record = data;
  },
  SET_LIVE_SESSION_LOADING(state, loading) {
    state.liveSessionLoading = loading;
  },
  SET_LIVE_SESSION_NOT_FOUND(state, notFound) {
    state.liveSession.notFound = notFound;
  },
  SET_MAP_CATEGORIES(state, data) {
    state.liveSession.mapCategories = data;
  },
  SET_MAP_CATEGORIES_LOADING(state, loading) {
    state.mapCategoriesLoading = loading;
  },
  SET_MESSAGES(state, data) {
    state.liveSession.messages = data;
  },
  SET_MESSAGE_TAB_LOADING(state, loading) {
    state.messagesTabLoading = loading;
  },
  SET_NOTE(state, note) {
    state.liveSession.note = note;
  },
  SET_NOTE_TAB_LOADING(state, loading) {
    state.noteTabLoading = loading;
  },
  RESET(state) {
    const initial = initialState();

    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  }
};

const actions = {
  async getLiveSession({ commit, state }, liveSessionId) {
    commit('SET_LIVE_SESSION_LOADING', true);

    const response = await API.getLiveSession(liveSessionId, state.currentGuestId);

    if (response.status === 200) {
      const { record } = response.data;

      if (_.isEmpty(record)) {
        commit('SET_LIVE_SESSION_NOT_FOUND', true);
      } else {
        commit('SET_LIVE_SESSION', record);
      }
    } else {
      commit('SET_LIVE_SESSION_NOT_FOUND', true);
    }

    commit('SET_LIVE_SESSION_LOADING', false);
  },
  async getMessages({ commit, state }, currentUserId) {
    commit('SET_MESSAGE_TAB_LOADING', true);

    const { data } = await API.getLiveSessionMessages(state.liveSession.record.id);
    const serializedResponse = [];

    data.records.forEach((message) => {
      const currentUser = currentUserId ? message.guestId === currentUserId : message.agent;

      serializedResponse.push({
        type: 'message',
        text: message.text,
        sender: message.sender,
        currentUser, // params.guestId
        time: moment(message.createdAt).format('hh:mm a'),
        agent: message.agent,
        visible: true
      });
    });

    commit('SET_MESSAGES', serializedResponse);
    commit('SET_MESSAGE_TAB_LOADING', false);
  },
  async getAttachments({ dispatch, state }) {
    await dispatch(
      'attachments/getAttachments',
      {
        apiModule: 'LIVE_SESSION',
        recordId: state.liveSession.record.id
      },
      { root: true }
    );
  },
  async getNote({ commit, state }, email) {
    commit('SET_NOTE_TAB_LOADING', true);

    let note = '';
    const prospectEmail = email || undefined;

    const response = await API.getLiveSessionNote(
      state.liveSession.record.id,
      { prospectEmail }
    );

    if (response.status === 200) {
      const { record: noteRecord } = response.data;
      note = noteRecord ? noteRecord.content : '';
    }

    commit('SET_NOTE', note);
    commit('SET_NOTE_TAB_LOADING', false);

    return note;
  },
  async getMapCategories({ commit }) {
    commit('SET_MAP_CATEGORIES_LOADING', true);

    const { data } = await userApi.getMapCategories();

    data.records.unshift({ title: 'None', key: 'none' });

    commit('SET_MAP_CATEGORIES', data.records);
    commit('SET_MAP_CATEGORIES_LOADING', false);
  },
  async getDefaultPins({ commit, state }) {
    const { data } = await API.getLiveSessionPins(state.liveSession.record.id);

    commit('SET_DEFAULT_PINS', data.records);
  },
  setCurrentGuestId({ commit }, guestId) {
    commit('SET_CURRENT_GUEST_ID', guestId);
  },
  getSessionBranding({ state, dispatch }) {
    dispatch(
      'brandingHeader/getBrandingHeader',
      { type: 'session', id: state.liveSession.record.id },
      { root: true }
    );
  },
  resetLiveSession({ commit }) {
    commit('RESET');
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions
};
